import React from "react";
import Layout from "@/components/utu/Layout";
import { GlassCard, GoldPillButton } from "@/components/utu/atoms";
import { SEO } from "@/components/SEO";
import { Binary, Compass, BarChart, FlaskConical, ArrowRight } from "lucide-react";
import { BOOKING_URL } from "@/mock";

export default function ResearchRnd() {
  return (
    <Layout>
      <SEO
        title="Research & R&D"
        description="Exploratory NLP research, low-resource dataset curation, Sheng morphology tokenizers, and custom neural network optimizations for computationally constrained environments."
        url="/services/research-rnd"
      />
      <section className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        {/* Header Hero */}
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/5 px-3 py-1 text-xs text-[hsl(var(--utu-gold))] backdrop-blur-xl mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--utu-gold))]" />
            Exploratory R&D Lab
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl mb-6">
            Research & R&D
          </h1>
          <p className="text-lg text-white/70 leading-relaxed">
            Developing foundational architectures and curated linguistic datasets to bridge the gap between global AI capabilities and low-resource African languages.
          </p>
        </div>

        {/* Core R&D Fields */}
        <div className="grid gap-6 md:grid-cols-2 mb-16">
          <GlassCard className="p-8">
            <FlaskConical className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Dialect Curation & Corpus Building</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              We curate ethnographically correct dialect datasets spanning Swahili, Sheng, Kikuyu, Luo, and other regional languages. We collect, clean, and align datasets that standard web-scrapers bypass.
            </p>
          </GlassCard>

          <GlassCard className="p-8">
            <Binary className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Custom Tokenizer Research</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              Commercial BPE models fail on agglutinative Bantu syntax, treating compound prefixes as independent words and bloating contexts. We research custom morphological segmenters to optimize token-to-word ratios.
            </p>
          </GlassCard>

          <GlassCard className="p-8">
            <Compass className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Code-Mixing Resolution</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              Urban communication is highly fluid, mixing Swahili, English, and vernacular in Sheng slang patterns. Our research focuses on mapping these rapid lexical swaps to stable semantic structures in real-time.
            </p>
          </GlassCard>

          <GlassCard className="p-8">
            <BarChart className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Resource-Constrained Optimization</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              We benchmark model architectures on local client configurations, exploring lightweight attention mechanisms, mixture-of-experts (MoE) optimizations, and quantization levels to maximize GPU throughput.
            </p>
          </GlassCard>
        </div>

        {/* Selected Publications/Focus Areas */}
        <GlassCard className="p-8 md:p-12 mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Current Laboratory Work</h2>
          <div className="space-y-6">
            <div className="border-b border-white/10 pb-6">
              <span className="text-xs font-semibold text-[hsl(var(--utu-gold))]">PROJECT BANTU-TOKEN</span>
              <h4 className="text-xl font-bold text-white mt-1 mb-2">Morphology-Aware Subword Segmenters</h4>
              <p className="text-sm text-white/60">Evaluating morphological decomposition models to serve as prefix filters prior to LLM embedding. Preliminary tests show prompt length decreases of 42% on Swahili test sets.</p>
            </div>
            <div className="border-b border-white/10 pb-6">
              <span className="text-xs font-semibold text-[hsl(var(--utu-gold))]">PROJECT SHENG-VEC</span>
              <h4 className="text-xl font-bold text-white mt-1 mb-2">Urban Slang Semantic Mapping</h4>
              <p className="text-sm text-white/60">Constructing lexical databases mapping Sheng word usage patterns in Nairobi to standard language intents, providing a cross-dialect translation matrix for enterprise search layers.</p>
            </div>
            <div>
              <span className="text-xs font-semibold text-[hsl(var(--utu-gold))]">PROJECT EDGE-MoE</span>
              <h4 className="text-xl font-bold text-white mt-1 mb-2">Quantized MoE Models on Local Private Hardware</h4>
              <p className="text-sm text-white/60">Benchmarking performance thresholds of 4-bit Mixtral-style architectures running on air-gapped corporate servers, optimizing prompt routing layers to prevent VRAM spikes.</p>
            </div>
          </div>
        </GlassCard>

        {/* Call to Action */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Collaborate on Language Research?</h3>
          <p className="text-sm text-white/60 mb-8 max-w-xl mx-auto">
            We collaborate with research institutions, NGOs, and enterprise groups seeking to build localized language tools or audit proprietary datasets.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <GoldPillButton className="h-12 px-8">
              Discuss Research Collaboration
              <ArrowRight className="h-4 w-4 ml-2" />
            </GoldPillButton>
          </a>
        </div>
      </section>
    </Layout>
  );
}
